import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRACK_KEYS = [
  { key: "entrepreneur", color: "#FF9F1C" },
  { key: "employment", color: "#567CB6" },
  { key: "hub", color: "#3663A8" },
  { key: "partner", color: "#E01E84" },
  { key: "volunteer", color: "#8C8C8C" },
];

export default function Register() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const steps = t("registerDeep.stepLabels", { returnObjects: true }) || ["Track", "Personal", "Background", "Motivation", "Review"];

  const [form, setForm] = useState({
    track: "",
    fullName: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    role: "",
    organisation: "",
    yearsExperience: "",
    background: "",
    motivation: "",
    referral: "",
    consent: false,
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const canNext = () => {
    if (step === 0) return !!form.track;
    if (step === 1) return form.fullName && form.email;
    if (step === 2) return form.country;
    if (step === 3) return form.motivation && form.motivation.length > 10;
    return true;
  };

  const submit = async () => {
    if (!form.consent) {
      toast.error(t("registerDeep.consentRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/registrations`, form);
      setDone(true);
      toast.success(t("registerDeep.submitSuccess"));
    } catch (e) {
      toast.error(t("registerDeep.submitError"));
    }
    setSubmitting(false);
  };

  const activeTrack = TRACK_KEYS.find((tr) => tr.key === form.track);

  if (done) {
    return (
      <div className="container-iyaf py-32 text-center" data-testid="register-done">
        <div className="w-20 h-20 mx-auto rounded-full bg-iyaf-blue text-white flex items-center justify-center mb-8">
          <Check size={32} />
        </div>
        <h1 className="font-display text-5xl font-extrabold tracking-tight mb-4">{t("registerDeep.doneTitle")}</h1>
        <p className="font-editorial text-2xl text-iyaf-ink/70 max-w-xl mx-auto">
          {t("registerDeep.doneBody")}
        </p>
      </div>
    );
  }

  return (
    <div className="container-iyaf py-16">
      <p className="eyebrow text-iyaf-blue mb-4">{t("registerDeep.eyebrow")}</p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-3">{t("registerDeep.title")}</h1>
      <p className="font-editorial text-xl text-iyaf-ink/70 max-w-2xl">
        {t("registerDeep.lead")}
      </p>

      {/* Stepper */}
      <div className="mt-10 mb-10 flex flex-wrap gap-3" data-testid="register-stepper">
        {steps.map((s, i) => {
          const active = i === step;
          const completed = i < step;
          return (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${completed ? "bg-iyaf-blue text-white" : active ? "bg-iyaf-ink text-white" : "bg-white border border-black/10 text-iyaf-ink/40"}`}>
                {completed ? <Check size={14} /> : i + 1}
              </div>
              <span className={`text-sm ${active ? "font-semibold" : "text-iyaf-ink/55"}`}>{s}</span>
              {i < steps.length - 1 && <div className="w-6 h-px bg-black/10 mx-2 hidden sm:block" />}
            </div>
          );
        })}
      </div>

      <div className="rounded-3xl bg-white border border-black/5 p-6 sm:p-10 min-h-[420px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.25 }}
          >
            {step === 0 && (
              <div data-testid="step-track">
                <p className="eyebrow text-iyaf-blue mb-3">{t("registerDeep.stepOf", { n: 1 })}</p>
                <h2 className="font-display text-3xl font-bold mb-2">{t("registerDeep.step0Title")}</h2>
                <p className="text-iyaf-ink/65 mb-8">{t("registerDeep.step0Body")}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {TRACK_KEYS.map((tr) => {
                    const active = form.track === tr.key;
                    return (
                      <button
                        key={tr.key}
                        type="button"
                        onClick={() => update("track", tr.key)}
                        data-testid={`track-${tr.key}`}
                        className={`text-left p-5 rounded-2xl border-2 transition-all ${active ? "border-current" : "border-black/5 hover:border-black/20"}`}
                        style={active ? { color: tr.color, background: `${tr.color}0F` } : {}}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: tr.color }} />
                          <span className="font-display font-semibold text-lg text-iyaf-ink">{t(`registerDeep.tracks.${tr.key}.title`)}</span>
                        </div>
                        <p className="text-sm text-iyaf-ink/65">{t(`registerDeep.tracks.${tr.key}.desc`)}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 1 && (
              <div data-testid="step-personal">
                <p className="eyebrow text-iyaf-blue mb-3">{t("registerDeep.stepOf", { n: 2 })}</p>
                <h2 className="font-display text-3xl font-bold mb-2">{t("registerDeep.step1Title")}</h2>
                <p className="text-iyaf-ink/65 mb-8">{t("registerDeep.step1Body")}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={t("registerDeep.fields.fullName")} testid="field-fullname" value={form.fullName} onChange={(v) => update("fullName", v)} required />
                  <Field label={t("registerDeep.fields.email")} type="email" testid="field-email" value={form.email} onChange={(v) => update("email", v)} required />
                  <Field label={t("registerDeep.fields.phone")} testid="field-phone" value={form.phone} onChange={(v) => update("phone", v)} />
                  <Field label={t("registerDeep.fields.role")} testid="field-role" value={form.role} onChange={(v) => update("role", v)} />
                  {activeTrack?.key === "partner" && (
                    <Field label={t("registerDeep.fields.organisation")} testid="field-organisation" value={form.organisation} onChange={(v) => update("organisation", v)} required />
                  )}
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-background">
                <p className="eyebrow text-iyaf-blue mb-3">{t("registerDeep.stepOf", { n: 3 })}</p>
                <h2 className="font-display text-3xl font-bold mb-2">{t("registerDeep.step2Title")}</h2>
                <p className="text-iyaf-ink/65 mb-8">{t("registerDeep.step2Body")}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={t("registerDeep.fields.country")} testid="field-country" value={form.country} onChange={(v) => update("country", v)} required />
                  <Field label={t("registerDeep.fields.city")} testid="field-city" value={form.city} onChange={(v) => update("city", v)} />
                  <Field label={t("registerDeep.fields.experience")} testid="field-experience" value={form.yearsExperience} onChange={(v) => update("yearsExperience", v)} />
                  <Field label={t("registerDeep.fields.background")} testid="field-background" value={form.background} onChange={(v) => update("background", v)} />
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-motivation">
                <p className="eyebrow text-iyaf-blue mb-3">{t("registerDeep.stepOf", { n: 4 })}</p>
                <h2 className="font-display text-3xl font-bold mb-2">{t("registerDeep.step3Title")}</h2>
                <p className="text-iyaf-ink/65 mb-8">{t("registerDeep.step3Body")}</p>
                <textarea
                  data-testid="field-motivation"
                  value={form.motivation}
                  onChange={(e) => update("motivation", e.target.value)}
                  rows={6}
                  required
                  placeholder={t("registerDeep.step3Placeholder")}
                  className="w-full p-4 rounded-2xl border border-black/10 focus:border-iyaf-blue outline-none resize-none font-sans"
                />
                <Field label={t("registerDeep.fields.referral")} testid="field-referral" value={form.referral} onChange={(v) => update("referral", v)} />
              </div>
            )}

            {step === 4 && (
              <div data-testid="step-review">
                <p className="eyebrow text-iyaf-blue mb-3">{t("registerDeep.stepOf", { n: 5 })}</p>
                <h2 className="font-display text-3xl font-bold mb-2">{t("registerDeep.step4Title")}</h2>
                <p className="text-iyaf-ink/65 mb-8">{t("registerDeep.step4Body")}</p>
                <dl className="space-y-3 mb-8">
                  <Row k={t("registerDeep.rowLabels.track")} v={activeTrack ? t(`registerDeep.tracks.${activeTrack.key}.title`) : form.track} notProvided={t("common.notProvided")} />
                  <Row k={t("registerDeep.rowLabels.name")} v={form.fullName} notProvided={t("common.notProvided")} />
                  <Row k={t("registerDeep.rowLabels.email")} v={form.email} notProvided={t("common.notProvided")} />
                  <Row k={t("registerDeep.rowLabels.country")} v={form.country} notProvided={t("common.notProvided")} />
                  {form.organisation && <Row k={t("registerDeep.rowLabels.organisation")} v={form.organisation} notProvided={t("common.notProvided")} />}
                  <Row k={t("registerDeep.rowLabels.motivation")} v={form.motivation} notProvided={t("common.notProvided")} />
                </dl>
                <label className="flex items-start gap-3 p-4 rounded-2xl bg-iyaf-paper border border-black/5 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="field-consent"
                    checked={form.consent}
                    onChange={(e) => update("consent", e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-sm text-iyaf-ink/75">
                    {t("registerDeep.consent")}
                  </span>
                </label>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex justify-between items-center">
          <button
            onClick={back}
            disabled={step === 0}
            data-testid="register-back"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-iyaf-ink/70 hover:text-iyaf-ink disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={15} /> {t("cta.back")}
          </button>
          {step < steps.length - 1 ? (
            <button
              onClick={next}
              disabled={!canNext()}
              data-testid="register-next"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-iyaf-ink text-white text-sm font-semibold hover:bg-iyaf-blue disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t("registerDeep.continue")} <ArrowRight size={15} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting || !form.consent}
              data-testid="register-submit"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-iyaf-blue text-white text-sm font-semibold hover:bg-iyaf-ink disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? t("registerDeep.submitting") : t("registerDeep.submit")}
              <Check size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-iyaf-ink/55 font-semibold">{label}{required && <span className="text-iyaf-magenta"> *</span>}</span>
      <input
        type={type}
        required={required}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full p-3 rounded-xl border border-black/10 focus:border-iyaf-blue outline-none"
      />
    </label>
  );
}

function Row({ k, v, notProvided }) {
  return (
    <div className="flex justify-between gap-6 py-2 border-b border-black/5 last:border-0">
      <dt className="text-iyaf-ink/55 text-sm shrink-0">{k}</dt>
      <dd className="text-iyaf-ink font-medium text-sm text-right">{v || notProvided || "Not provided"}</dd>
    </div>
  );
}
